/**
 * Strips leading/trailing commas and whitespace — fixes bulk-import artifacts like "San Antonio,"
 */
export const sanitizeCity = (city) =>
    (city || '').trim().replace(/^[,\s]+|[,\s]+$/g, '').trim();

/**
 * Decodes common HTML entities that web imports sometimes store literally (e.g. &amp; → &)
 */
export const decodeHtmlEntities = (str) => {
    if (!str) return '';
    return String(str)
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&apos;/g, "'");
};

/**
 * Formats a phone number for display using E.164-style notation (+[country] XXX-XXX-XXXX).
 * - 10-digit US numbers → +1 210-555-2001
 * - 11-digit E.164 stored as +1XXXXXXXXXX → +1 210-555-2001
 * - Future international numbers (e.g. +44...) → returned as-is from DB
 * Edit screens store and accept raw digits only — this is for display only.
 */
export const formatPhone = (phone) => {
    if (phone === null || phone === undefined || phone === '') return '';
    let digits = String(phone).replace(/\D/g, '');
    // US: 10 digits or 11 digits starting with 1
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.substring(1);
    if (digits.length === 10) {
        return `+1 ${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    // Non-US or unparseable — return as-is so nothing is lost
    return String(phone);
};

export const normalizePhoneDigits = (phone) => {
    if (phone === null || phone === undefined || phone === '') return '';
    let digits = String(phone).replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.substring(1);
    return digits;
};

export const validateName = (name) => {
    return {
        isValid: /^[A-Za-z\s&'\-]*$/.test(name.trim()) && name.length > 0,
        validChars: /^[A-Za-z\s&'\-]*$/.test(name.trim()),
    }
};

export const validatePhone = (phone) => {
    return {
        isValid: /^\d{10}$/.test(phone.trim()),
        validChars: /^\d{0,10}$/.test(phone.trim()),
    };
};

export const validateCityState = (input) => { 
    return {
        isValid: /^[A-Za-z\s]*$/.test(input.trim()) && input.length > 0,
        validChars: /^[A-Za-z\s]*$/.test(input.trim()),
    }
};


export const validateGateCode = (value) => {
    return {
      validChars: true,
      isValid: value.length > 0,
    };
};

export const validateAddress = (address) => {
    return {
        isValid: /^[A-Za-z0-9\s#]*$/.test(address.trim()) && address.length > 0,
        validChars: /^[A-Za-z0-9\s#]*$/.test(address.trim()),
    }
};

export const validateZip = (zip) => {
    return {
        isValid: /^\d{5}$/.test(zip.trim()),
        validChars: /^\d{0,5}$/.test(zip.trim()),
    };
};

export const validateEmail = (email) => {
    return {
        isValid: /^(?!.*\.\.)([A-Za-z0-9]+([._%+-][A-Za-z0-9]+)*)@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/.test(email.trim()),
        validChars: /^[A-Za-z0-9@._%+-]*$/.test(email.trim()),
    };
};

export const validatePoolDimensions = (value) => {
    return /^\d*\.?\d*$/.test(value.trim()) && value.length > 0;
};

// export const validateShape = (shape) => {
//     const shapeOptions = [
//         'Circle',
//         'Square',
//         'Rectangle',
//         'Oval',
//         'Kidney',
//         'L-Shape',
//         'Figure-8',
//         'Roman',
//         'Freeform',
//     ];
//     return shapeOptions.includes(shape);
// };

export const validateOccurrence = (occurrence) => {
    const occurrenceMap = {
        "One time": 0,         
        "Daily": 1,
        "Weekly": 7,
        "Every other week": 14,
        "Once a month": 30,
        "Quarterly": 90,
        "Twice a year": 180,
        "Yearly": 365
    };
    const finalOccurrence = occurrenceMap[occurrence] ?? null;
    return finalOccurrence !== null && finalOccurrence !== undefined && finalOccurrence >= 0;
};

export const validatePersonDetails = (formData) => {
    return validateName(formData.firstName).isValid && validateName(formData.lastName).isValid && 
    validatePhone(formData.phoneNumber).isValid && validateCityState(formData.city).isValid && 
    formData.address.trim() && validateCityState(formData.state).isValid && 
    validateZip(formData.zipCode).isValid && validateEmail(formData.email).isValid;
};

export const validatePoolDetails = (formData, volume) => {
    const anyFilled = formData.poolType || formData.length || formData.width || formData.height || formData.shape;
    const allFilled = !!(
        formData.poolType?.trim() &&
        formData.length?.trim() &&
        formData.width?.trim() &&
        formData.height?.trim() &&
        formData.shape?.trim() &&
        volume
    );
    return {
        isValid: !anyFilled || allFilled,
        showRequired: !!anyFilled
    };
};

export const validateWorkOrderDetails = (technician, task, occurrence, date, comments) => {
    const anyFilled = !!(
        technician || task || occurrence || date || (task === "On Demand" && comments?.trim())
      );
    const allFilled = !!(
        technician &&
        task &&
        validateOccurrence(occurrence) &&
        date &&
        (task !== "On Demand" || comments?.trim())
      );  
    return {
        isValid: !anyFilled || allFilled,
        showRequired: !!anyFilled,
        showCommentsRequired: task === "On Demand"
    };
};
